/** Complete server card display and respondent-count/score invariants. */
import * as z from 'zod';
const text = z.string();
const nullableText = text.nullable();
const tone = z.enum(['info', 'success', 'warning', 'critical']);
/** One cell per accepted primary respondent; values are server-validated continuous contributions. */
export const resultRespondentGridSchema = z.discriminatedUnion('status', [
    z
        .strictObject({
        status: z.literal('available'),
        respondentCount: z.number().int().positive(),
        countLabel: text,
        explanation: text,
        accessibilityLabel: text,
        respondents: z
            .array(z.strictObject({
            id: text.min(1),
            fillPercent: z.number().min(0).max(100),
            accessibilityLabel: text,
        }))
            .min(1),
    })
        .superRefine((grid, ctx) => {
        if (grid.respondents.length !== grid.respondentCount ||
            new Set(grid.respondents.map((person) => person.id)).size !== grid.respondentCount)
            ctx.addIssue({
                code: 'custom',
                message: 'Respondent grid count and unique identities must match',
            });
    }),
    z.strictObject({
        status: z.literal('unavailable'),
        reason: text.min(1),
        message: text,
    }),
]);
export const resultTestedContentSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('price'), value: text, detail: text }),
    z.strictObject({
        kind: z.literal('text'),
        segments: z.array(z.strictObject({ text, highlighted: z.boolean() })),
        detail: text,
    }),
    z.strictObject({
        kind: z.literal('photo'),
        src: text,
        alt: text,
        detail: text,
    }),
    z.strictObject({ kind: z.literal('unavailable'), message: text }),
]);
export const resultTreatmentCardSchema = z
    .strictObject({
    armId: text,
    label: text,
    roleLabel: text,
    isBaseline: z.boolean(),
    productTitle: text,
    productDescription: text,
    isDescriptionArm: z.boolean(),
    wordCount: z.number().int().nonnegative(),
    imageUrl: nullableText,
    imageCaption: nullableText,
    price: nullableText,
    claim: nullableText,
    offer: nullableText,
    changedFields: z.array(z.enum(['description', 'image', 'price', 'claim', 'promotion'])),
    t2bPercent: nullableText,
    confidenceLabel: nullableText,
    state: z.strictObject({
        tone: z.enum(['positive', 'caution', 'neutral']),
        label: text,
        icon: z.enum(['check', 'next', 'none']),
    }),
    isRecommended: z.boolean(),
    badge: z.strictObject({ text, tone }).nullable(),
    badges: z.array(text),
    notices: z.array(text),
    pending: z.boolean(),
    metricLabel: text,
    metricValue: text,
    priceLabel: text,
    imageAlt: text,
    noImageText: text,
    wordCountLabel: nullableText,
    detailLabels: z.strictObject({
        imageCaption: text,
        claim: text,
        offer: text,
    }),
    reactionAction: z.strictObject({ tabId: text, label: text }).nullable(),
    content: resultTestedContentSchema,
    metricProgress: z.number().min(0).max(100).nullable(),
    metricAccessibilityLabel: text,
    respondentGrid: resultRespondentGridSchema,
})
    .superRefine((card, ctx) => {
    if (card.respondentGrid.status !== 'available')
        return;
    const average = card.respondentGrid.respondents.reduce((sum, person) => sum + person.fillPercent, 0) /
        card.respondentGrid.respondentCount;
    if (card.metricProgress === null || Math.abs(average - card.metricProgress) > 1e-6)
        ctx.addIssue({
            code: 'custom',
            message: 'Respondent contributions must reproduce the displayed metric',
        });
});
//# sourceMappingURL=shopify-study-results-card.js.map